import { ENavigationItemSegmentation } from 'core/model/enums/hierarchy.enum';
import {
  IHierarchyTreeItem,
  THierarchyItemCreateForm,
  THierarchyItemEditForm,
  THierarchyTreeSubitem,
} from 'core/model/interfaces/navigation/hierarchy.interface';
import {
  IGetHierarchyItemsResponse,
  IGetHierarchySubitemsResponse,
  IPostHierarchyTreeSubitemBody,
  IPostINavigationTreeItemBody,
  IPutHierarchyTreeSubitemBody,
  IPutINavigationTreeItemBody,
} from './hierarchy.dto';
import { getHierarchyAssociation } from './hierarchy.utils';

export const mapHierarchyItems = (
  treeId: string,
  treeResponse: IGetHierarchyItemsResponse,
): Array<IHierarchyTreeItem> => {
  const mapped = treeResponse.items.map(
    item =>
      ({
        treeId,
        expanded: false,
        id: `${item.id}`,
        appImage: { url: item.imageUrl },
        webIcon: { url: item.iconUrl },
        name: item.name,
        segmentationType: `${item.segmentationTypeId}` as ENavigationItemSegmentation,
        navigationSubitems: item.subitems,
      } as IHierarchyTreeItem),
  );
  return mapped;
};

export const mapHierarchySubitems = (
  treeId: string,
  itemId: string,
  subitemsResponse: IGetHierarchySubitemsResponse,
): Array<THierarchyTreeSubitem> => {
  const mapped = subitemsResponse.subItems?.map(
    subitem =>
      ({
        id: `${subitem.id}`,
        name: subitem.name,
        categories: subitem.productCategories?.map(cat => cat.id) || [],
        families: subitem.productCategories?.flatMap(cat => cat.productFamilyIds?.map(fam => fam) || []) || [],
        treeId,
        itemId: `${itemId}`,
        productIds: subitem.productIds ? subitem.productIds.join(',') : '',
        submitemType: subitem.productCategories
          ? ENavigationItemSegmentation.BY_CATEGORIES_AND_FAMILIES
          : ENavigationItemSegmentation.BY_PRODUCT_IDS,
      } as THierarchyTreeSubitem),
  );
  return mapped;
};

export const toPostHierarchyItemBody = (body: THierarchyItemCreateForm): FormData => {
  const formattedBody: Partial<IPostINavigationTreeItemBody> = {
    name: body.name,
    segmentationTypeId: `${body.segmentationType}`,
  };
  if (body.appImage?.blob) formattedBody.image = body.appImage.blob;
  if (body.webIcon?.blob) formattedBody.icon = body.webIcon.blob;

  const formData = new FormData();

  Object.entries(formattedBody).forEach(([key, value]) => {
    if (value) {
      formData.append(key, value);
    }
  });
  return formData;
};

export const toPutHierarchyItemBody = (body: THierarchyItemEditForm): FormData => {
  const formattedBody: Partial<IPutINavigationTreeItemBody> = {
    name: body.name,
  };
  if (body.appImage?.blob) formattedBody.image = body.appImage.blob;
  if (body.webIcon?.blob) formattedBody.icon = body.webIcon.blob;
  // A file hasn't been selected or it has been removed
  if (!body.webIcon?.url) formattedBody.icon = null;

  const formData = new FormData();

  Object.entries(formattedBody).forEach(([key, value]) => {
    if (value) {
      formData.append(key, value);
    } else {
      formData.append(key, '');
    }
  });
  return formData;
};

export const toPostHierarchyTreeSubitemBody = (
  body: Omit<THierarchyTreeSubitem, 'id'>,
): IPostHierarchyTreeSubitemBody => ({
  name: body.name,
  ...getHierarchyAssociation(body),
  treeId: body.treeId,
  itemId: body.itemId,
  segmentationTypeId: Number(body.submitemType),
});

export const toPutHierarchySubitemBody = (body: THierarchyTreeSubitem): IPutHierarchyTreeSubitemBody => ({
  subItemId: Number(body.id),
  name: body.name,
  ...getHierarchyAssociation(body),
  treeId: body.treeId,
  itemId: body.itemId,
  segmentationTypeId: Number(body.submitemType),
});
